import type {
  ComponentOptionsMixin,
  DefineComponent,
  ExtractDefaultPropTypes,
  PublicProps,
} from 'vue';
import { defineComponent, h } from 'vue';

import { excludeKeys, pickExistingKeys } from '@/kasten-components/utils';

import KaVeeFormField from './KaVeeFormField.vue';
import type { ControlProps, KaVeeFormGroupProps } from './types';

export type ClassConstructor = abstract new (...args: any) => any;

// Exclude callbacks the list as it's going to be merged and forwarded, thus callbacks are not considered to be a conflict
export type TransitivePropNamesFromFormGroupToComponent = Exclude<
  keyof ControlProps,
  `on${string}`
>;

type ComponentProps<
  A extends ComponentOptionsMixin & (abstract new (...args: any) => any),
  FP extends Partial<KaVeeFormGroupProps> = Record<string, never>,
  CP extends Partial<InstanceType<A>['$props']> = Record<string, never>,
  AP extends InstanceType<A> = InstanceType<A>,
> = Omit<AP['$props'], TransitivePropNamesFromFormGroupToComponent | keyof FP | keyof CP> &
  KaVeeFormGroupProps;

// When extracted the type of Slots comes with { [name: string]: Slot<any> }.
// Example of Input component:
// Readonly<{
//     [name: string]: import("vue").Slot<any>;
// }> & {
//     label?(_: {
//         label: string;
//     }): any;
//     description?(_: {
//         description: string;
//     }): any;
// }
// This messed with validation of slots used in the app.
// RemoveArbitraryKey removes type of arbitrary key leaving only types of original slots.
type RemoveArbitraryKey<T extends Record<string, any>> = {
  [K in keyof T as string extends K ? never : K]: T[K];
};

// vue-tsc manually includes this type to generated type of component
type TemplateSlots<S> = {
  new (): {
    $slots: S;
  };
};

type MergeComponentDefinitionsWithKaVeeFormGroup<
  A extends ComponentOptionsMixin & ClassConstructor,
  FP extends Partial<KaVeeFormGroupProps>,
  CP extends Partial<InstanceType<A>['$props']>,
  AP extends InstanceType<A> = InstanceType<A>,
> = DefineComponent<
  ComponentProps<A, FP, CP>,
  Record<string, never>,
  unknown,
  Record<string, never>,
  Record<string, never>,
  ComponentOptionsMixin,
  ComponentOptionsMixin,
  AP['$emit'],
  string,
  PublicProps,
  ComponentProps<A, FP, CP>,
  ExtractDefaultPropTypes<ComponentProps<A, FP, CP>>,
  RemoveArbitraryKey<AP['$slots']>
> &
  TemplateSlots<RemoveArbitraryKey<AP['$slots']>>;

// This is a workaround way to tight a union of strings to a physical representation.
// It's not possible to use string union to force string tuple to define every case of a union
// because order of union elements is indeterminate, meaning such a contract would randomly break the type check.
const ControlPropsToEncapsulate = Object.keys({
  modelValue: null,
  name: null,
  errorMessage: null,
  value: null,
} satisfies Record<Exclude<keyof ControlProps, `on${string}`>, null>);

const isCallback = <O extends Record<string, unknown>>(object: O, propName: keyof O) =>
  typeof propName === 'string' &&
  propName.startsWith('on') &&
  typeof object[propName] === 'function';

const capitalize = (str: string) => {
  if (str === '') return '';

  return str.slice(0, 1).toUpperCase() + str.slice(1);
};

// Group callbacks of the same name to invoke them all
const mergeCallbacks = (chunks: Array<Record<string, any>>) => {
  const callbackGroups: Record<string, ((...args: any[]) => any)[]> = {};

  for (const chunk of chunks) {
    for (const propName in chunk) {
      if (!isCallback(chunk, propName)) continue;

      if (!(propName in callbackGroups)) callbackGroups[propName] = [];

      callbackGroups[propName].push(chunk[propName]);
    }
  }

  const mergedCallbacks: Record<string, (...args: any[]) => any> = {};

  for (const callbackName in callbackGroups) {
    mergedCallbacks[callbackName] = (...args: any[]) =>
      callbackGroups[callbackName].forEach((func) => func(...args));
  }

  return mergedCallbacks;
};

const extractKaVeeFormProps = (props: Record<string, any>) =>
  pickExistingKeys(props, Object.keys(KaVeeFormField.props)) as KaVeeFormGroupProps;

export const veeFormFieldHOC = <
  C extends ComponentOptionsMixin & ClassConstructor,
  FP extends Partial<KaVeeFormGroupProps> = Record<string, never>,
  CP extends Partial<InstanceType<C>['$props']> = Record<string, never>,
>(
  Component: C,
  predefinedProps?: {
    KaVeeForm?: FP;
    Component?: CP;
  },
) => {
  const extractComponentProps = (props: Record<string, any>) =>
    pickExistingKeys(props, Object.keys(Component.props));
  const componentEmits = (Component.emits as string[]) ?? [];

  return defineComponent({
    name: (Component.name ?? Component.__name ?? '') + 'Vee',
    components: {
      KaVeeFormField,
      Component,
    },
    props: {
      ...excludeKeys(Component.props, [
        ...ControlPropsToEncapsulate,
        ...Object.keys(predefinedProps?.Component ?? {}),
      ]),
      ...excludeKeys(KaVeeFormField.props, Object.keys(predefinedProps?.KaVeeForm ?? {})),
    },
    emits: componentEmits,
    methods: {
      getCbForwards() {
        return componentEmits.reduce(
          (acc, eventName) => ({
            ...acc,
            ['on' + capitalize(eventName)]: (...args: any) => this.$emit(eventName, ...args),
          }),
          {},
        );
      },
    },
    render() {
      const componentProps = extractComponentProps(this.$props);

      return h(
        KaVeeFormField,
        { ...extractKaVeeFormProps(this.$props), ...predefinedProps?.KaVeeForm },
        {
          default: (formGroupSlotProps: any) =>
            h(
              Component,
              {
                ...this.$attrs,
                ...componentProps,
                ...formGroupSlotProps,
                ...predefinedProps?.Component,
                ...mergeCallbacks([
                  predefinedProps?.Component,
                  componentProps,
                  formGroupSlotProps,
                  this.getCbForwards(),
                ]),
              },
              Object.entries(this.$slots).reduce(
                (acc, [slotName]) => ({
                  ...acc,
                  [slotName]: (props: any) => this.$slots[slotName]?.(props),
                }),
                {},
              ),
            ),
        },
      );
    },
  }) as unknown as MergeComponentDefinitionsWithKaVeeFormGroup<C, FP, CP>;
};
